import { Save } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Button } from '@/components/occurrences/button'
import {
	ClassificationChip,
	ClassificationHelp,
	Field,
	Notice,
	Panel,
	PhotoPlaceholder,
	PotentialPill,
	ReadOnlyValue,
} from '@/components/occurrences/occurrence-ui'
import { PotentialPyramid } from '@/components/occurrences/potential-pyramid'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import {
	BODY_PARTS,
	BRANCHES,
	CLASSIFICATIONS,
	CLASSIFICATIONS_BY_TYPE,
	CONTRACTS,
	EDUCATION,
	FREQUENCIES,
	JOB_ROLES,
	LIFE_ATTITUDES,
	PEOPLE,
	SERVICE_TYPES,
	WORK_TYPES,
} from '@/mocks/occurrences/catalog'
import { can, canViewConfidentiality } from '@/mocks/occurrences/rules'
import { useOccurrenceMock } from '@/mocks/occurrences/store'
import type {
	ClassificationCode,
	Occurrence,
	Potential,
} from '@/types/occurrence'
import { ACTION_STATUS_LABEL, TYPE_LABEL } from '@/types/occurrence'
import { useOccurrence } from './occurrence-context'

const br = (d: string) => (d ? d.split('-').reverse().join('/') : '—')

// ─── Resumo (painel da ocorrência) ──────────────────────────────────────────
export const SummarySection = () => {
	const { occ, profile } = useOccurrence()
	const navigate = useNavigate()
	const photo = occ.evidences.find(
		(e) =>
			!e.deletedReason && canViewConfidentiality(profile, e.confidentiality),
	)
	const roots = occ.investigation.causes.filter((c) => c.isRoot)
	const base = `/occurrences/${occ.id}`

	return (
		<div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
			<div className="space-y-4 xl:col-span-2">
				<Panel>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-[180px_1fr]">
						{photo ? (
							<PhotoPlaceholder
								tone={photo.tone}
								className="h-36 w-full rounded-lg sm:h-full"
								label={photo.caption}
							/>
						) : (
							<div className="flex h-36 items-center justify-center rounded-lg bg-muted/40 text-xs text-muted-foreground">
								Sem foto
							</div>
						)}
						<div className="space-y-3">
							<div className="flex flex-wrap items-center gap-2">
								<PotentialPill value={occ.potential} />
								<ClassificationChip
									code={occ.classification ?? occ.proposedClassification}
								/>
								{!occ.classification && occ.proposedClassification && (
									<span className="text-xs text-muted-foreground">
										(proposta, aguardando confirmação)
									</span>
								)}
							</div>
							<p className="text-sm leading-relaxed text-kaefer-dark-grey">
								{occ.fullDescription || occ.description || 'Sem descrição.'}
							</p>
							<div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
								<span>
									<b>Parte do corpo:</b> {occ.bodyPart}
								</span>
								<span>
									<b>Atitude pela vida:</b> {occ.lifeAttitude}
								</span>
							</div>
						</div>
					</div>
				</Panel>

				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<Panel
						title="Causas"
						actions={
							<Button
								variant="ghost"
								size="sm"
								onClick={() => navigate(`${base}/investigation`)}
							>
								Abrir
							</Button>
						}
					>
						{roots.length === 0 ? (
							<p className="text-sm text-muted-foreground">
								Nenhuma causa-raiz identificada.
							</p>
						) : (
							<ul className="space-y-2 text-sm">
								{roots.map((c) => (
									<li key={c.id} className="border-b pb-2 last:border-0">
										{c.text}
									</li>
								))}
							</ul>
						)}
					</Panel>
					<Panel
						title="Ações principais"
						actions={
							<Button
								variant="ghost"
								size="sm"
								onClick={() => navigate(`${base}/actions`)}
							>
								Abrir
							</Button>
						}
					>
						{occ.actions.length === 0 ? (
							<p className="text-sm text-muted-foreground">
								Nenhuma ação cadastrada.
							</p>
						) : (
							<ul className="space-y-2 text-sm">
								{occ.actions.slice(0, 4).map((a) => (
									<li
										key={a.id}
										className="flex items-start justify-between gap-2 border-b pb-2 last:border-0"
									>
										<span className="line-clamp-2">{a.description}</span>
										<span className="shrink-0 text-xs text-muted-foreground">
											{ACTION_STATUS_LABEL[a.status]}
										</span>
									</li>
								))}
							</ul>
						)}
					</Panel>
				</div>
			</div>

			<div className="space-y-4">
				<Panel title="Identificação">
					<div className="grid grid-cols-2 gap-3">
						<ReadOnlyValue label="Data" value={`${br(occ.date)} ${occ.time}`} />
						<ReadOnlyValue label="Tipo" value={TYPE_LABEL[occ.type]} />
						<ReadOnlyValue label="Filial" value={occ.branch} />
						<ReadOnlyValue label="Contrato" value={occ.contract} />
						{occ.employee && (
							<>
								<ReadOnlyValue label="Colaborador" value={occ.employee.name} />
								<ReadOnlyValue label="Função" value={occ.employee.jobRole} />
								<ReadOnlyValue label="PDA" value={occ.employee.pda} />
							</>
						)}
					</div>
				</Panel>
			</div>
		</div>
	)
}

// ─── Dados da ocorrência ────────────────────────────────────────────────────
export const DataSection = () => {
	const { occ, editable, locked, profile } = useOccurrence()
	const update = useOccurrenceMock((s) => s.update)
	const [f, setF] = useState(() => ({ ...occ }))
	const [saved, setSaved] = useState(false)
	const set = <K extends keyof Occurrence>(k: K, v: Occurrence[K]) => {
		setSaved(false)
		setF((s) => ({ ...s, [k]: v }))
	}
	// Campos críticos ficam bloqueados após a validação; só Gestor SSMA edita
	const critical = locked && !can(profile, 'confirmClassification')
	const ro = !editable
	const roCrit = ro || critical

	const save = () => {
		const keys: (keyof Occurrence)[] = [
			'date',
			'time',
			'branch',
			'contract',
			'description',
			'fullDescription',
			'bodyPart',
			'lifeAttitude',
			'equated',
			'workRelated',
			'state',
			'city',
			'cep',
			'activityFrequency',
			'workType',
			'serviceType',
			'mainActivity',
			'workSchedule',
		]
		const changed = keys.filter((k) => f[k] !== occ[k])
		if (changed.length === 0) return setSaved(true)
		const patch = Object.fromEntries(changed.map((k) => [k, f[k]]))
		update(occ.id, patch, 'Dados da ocorrência alterados', {
			detail: `Campos: ${changed.join(', ')}`,
			diff:
				changed.length === 1
					? {
							field: String(changed[0]),
							from: String(occ[changed[0]]),
							to: String(f[changed[0]]),
						}
					: undefined,
		})
		setSaved(true)
	}

	return (
		<div className="space-y-4">
			{critical && (
				<Notice tone="lock">
					Investigação validada: campos críticos bloqueados. Alterações exigem o
					Gestor SSMA (justificativa e nova versão).
				</Notice>
			)}
			<Panel title="Dados da ocorrência">
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
					<Field label="Data" required>
						<Input
							type="date"
							disabled={roCrit}
							value={f.date}
							max={new Date().toISOString().slice(0, 10)}
							onChange={(e) => set('date', e.target.value)}
						/>
					</Field>
					<Field label="Hora da ocorrência">
						<Input
							type="time"
							disabled={roCrit}
							value={f.time}
							onChange={(e) => set('time', e.target.value)}
						/>
					</Field>
					<Field label="Horário de trabalho">
						<Input
							type="time"
							disabled={ro}
							value={f.workSchedule}
							onChange={(e) => set('workSchedule', e.target.value)}
						/>
					</Field>
					<Field label="Filial" required>
						<NativeSelect
							disabled={roCrit}
							value={f.branch}
							onChange={(e) => {
								set('branch', e.target.value)
								set('contract', '')
							}}
						>
							{BRANCHES.map((b) => (
								<NativeSelectOption key={b} value={b}>
									{b}
								</NativeSelectOption>
							))}
						</NativeSelect>
					</Field>
					<Field label="Contrato">
						<NativeSelect
							disabled={roCrit}
							value={f.contract}
							onChange={(e) => set('contract', e.target.value)}
						>
							<NativeSelectOption value="">—</NativeSelectOption>
							{(CONTRACTS[f.branch] ?? []).map((c) => (
								<NativeSelectOption key={c} value={c}>
									{c}
								</NativeSelectOption>
							))}
						</NativeSelect>
					</Field>
					<Field label="Estado">
						<Input
							disabled={ro}
							value={f.state}
							onChange={(e) => set('state', e.target.value)}
						/>
					</Field>
					<Field label="Cidade onde ocorreu">
						<Input
							disabled={ro}
							value={f.city}
							onChange={(e) => set('city', e.target.value)}
						/>
					</Field>
					<Field label="CEP">
						<Input
							disabled={ro}
							value={f.cep}
							onChange={(e) => set('cep', e.target.value)}
						/>
					</Field>
					<Field label="Atitudes pela vida aplicada">
						<NativeSelect
							disabled={ro}
							value={f.lifeAttitude}
							onChange={(e) => set('lifeAttitude', e.target.value)}
						>
							{LIFE_ATTITUDES.map((a) => (
								<NativeSelectOption key={a} value={a}>
									{a}
								</NativeSelectOption>
							))}
						</NativeSelect>
					</Field>
					<Field label="Frequência da atividade">
						<NativeSelect
							disabled={ro}
							value={f.activityFrequency}
							onChange={(e) => set('activityFrequency', e.target.value)}
						>
							{FREQUENCIES.map((a) => (
								<NativeSelectOption key={a} value={a}>
									{a}
								</NativeSelectOption>
							))}
						</NativeSelect>
					</Field>
					<Field label="Tipo da obra">
						<NativeSelect
							disabled={ro}
							value={f.workType}
							onChange={(e) => set('workType', e.target.value)}
						>
							{WORK_TYPES.map((a) => (
								<NativeSelectOption key={a} value={a}>
									{a}
								</NativeSelectOption>
							))}
						</NativeSelect>
					</Field>
					<Field label="Tipo do serviço">
						<NativeSelect
							disabled={ro}
							value={f.serviceType}
							onChange={(e) => set('serviceType', e.target.value)}
						>
							{SERVICE_TYPES.map((a) => (
								<NativeSelectOption key={a} value={a}>
									{a}
								</NativeSelectOption>
							))}
						</NativeSelect>
					</Field>
					<Field label="Parte do corpo atingida">
						<NativeSelect
							disabled={ro}
							value={f.bodyPart}
							onChange={(e) => set('bodyPart', e.target.value)}
						>
							{BODY_PARTS.map((a) => (
								<NativeSelectOption key={a} value={a}>
									{a}
								</NativeSelectOption>
							))}
						</NativeSelect>
					</Field>
					<Field label="Atividade principal">
						<NativeSelect
							disabled={ro}
							value={f.mainActivity ? 'Sim' : 'Não'}
							onChange={(e) => set('mainActivity', e.target.value === 'Sim')}
						>
							<NativeSelectOption>Sim</NativeSelectOption>
							<NativeSelectOption>Não</NativeSelectOption>
						</NativeSelect>
					</Field>
					<Field label="Relacionada ao trabalho">
						<NativeSelect
							disabled={ro}
							value={f.workRelated ? 'Sim' : 'Não'}
							onChange={(e) => set('workRelated', e.target.value === 'Sim')}
						>
							<NativeSelectOption>Sim</NativeSelectOption>
							<NativeSelectOption>Não</NativeSelectOption>
						</NativeSelect>
					</Field>
					<Field
						label="Equiparado"
						hint="CAT aberta, mas não considerada nas estatísticas"
					>
						<NativeSelect
							disabled={ro}
							value={f.equated ? 'Sim' : 'Não'}
							onChange={(e) => set('equated', e.target.value === 'Sim')}
						>
							<NativeSelectOption>Sim</NativeSelectOption>
							<NativeSelectOption>Não</NativeSelectOption>
						</NativeSelect>
					</Field>
				</div>
				<Field label="Descrição breve" required>
					<Input
						disabled={roCrit}
						value={f.description}
						onChange={(e) => set('description', e.target.value)}
					/>
				</Field>
				<Field
					label="Descrição completa"
					hint={`${f.fullDescription.length}/2000 caracteres`}
				>
					<Textarea
						rows={6}
						maxLength={2000}
						disabled={roCrit}
						value={f.fullDescription}
						onChange={(e) => set('fullDescription', e.target.value)}
					/>
				</Field>
				{!ro && (
					<div className="flex items-center justify-end gap-3">
						{saved && (
							<span className="text-sm text-kaefer-forest-green">
								Salvo e registrado na auditoria
							</span>
						)}
						<Button
							variant="primary"
							className="gap-2 font-semibold"
							onClick={save}
						>
							<Save className="size-4" /> Salvar
						</Button>
					</div>
				)}
			</Panel>
		</div>
	)
}

// ─── Colaborador ────────────────────────────────────────────────────────────
export const EmployeeSection = () => {
	const { occ, editable } = useOccurrence()
	const update = useOccurrenceMock((s) => s.update)
	const [e, setE] = useState(
		occ.employee ?? {
			name: '',
			badge: '',
			jobRole: '',
			admission: '',
			age: null as number | null,
			education: '',
			pda: '',
		},
	)
	const [saved, setSaved] = useState(false)
	const set = <K extends keyof typeof e>(k: K, v: (typeof e)[K]) => {
		setSaved(false)
		setE((s) => ({ ...s, [k]: v }))
	}

	const pick = (badge: string) => {
		const p = PEOPLE.find((x) => x.badge === badge)
		if (p)
			setE((s) => ({
				...s,
				name: p.name,
				badge: p.badge,
				jobRole: p.jobRole,
				admission: p.admission,
				age: p.age,
				education: p.education,
			}))
	}

	return (
		<Panel
			title="Dados do colaborador"
			description="Origem: base de pessoas sincronizada com o Protheus. PDA e instrução são complementados manualmente."
		>
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
				<Field
					label="Nome do colaborador"
					required
					hint="* Necessário para abrir a CAT"
				>
					<NativeSelect
						disabled={!editable}
						value={e.badge}
						onChange={(ev) => pick(ev.target.value)}
					>
						<NativeSelectOption value="">
							Localizar colaborador
						</NativeSelectOption>
						{PEOPLE.map((p) => (
							<NativeSelectOption key={p.badge} value={p.badge}>
								{p.name}
							</NativeSelectOption>
						))}
					</NativeSelect>
				</Field>
				<Field label="Matrícula">
					<Input disabled value={e.badge} />
				</Field>
				<Field label="Data de admissão">
					<Input
						type="date"
						disabled={!editable}
						value={e.admission}
						onChange={(ev) => set('admission', ev.target.value)}
					/>
				</Field>
				<Field label="Função">
					<NativeSelect
						disabled={!editable}
						value={e.jobRole}
						onChange={(ev) => set('jobRole', ev.target.value)}
					>
						<NativeSelectOption value="">—</NativeSelectOption>
						{JOB_ROLES.map((j) => (
							<NativeSelectOption key={j} value={j}>
								{j}
							</NativeSelectOption>
						))}
					</NativeSelect>
				</Field>
				<Field label="Idade">
					<Input
						type="number"
						disabled={!editable}
						value={e.age ?? ''}
						onChange={(ev) =>
							set('age', ev.target.value ? Number(ev.target.value) : null)
						}
					/>
				</Field>
				<Field label="PDA do colaborador" hint="Apenas o número. Ex.: 93">
					<Input
						disabled={!editable}
						value={e.pda}
						onChange={(ev) => set('pda', ev.target.value.replace(/\D/g, ''))}
					/>
				</Field>
				<Field label="Instrução acadêmica" className="sm:col-span-2">
					<NativeSelect
						disabled={!editable}
						value={e.education}
						onChange={(ev) => set('education', ev.target.value)}
					>
						<NativeSelectOption value="">—</NativeSelectOption>
						{EDUCATION.map((j) => (
							<NativeSelectOption key={j} value={j}>
								{j}
							</NativeSelectOption>
						))}
					</NativeSelect>
				</Field>
			</div>
			{editable && (
				<div className="flex items-center justify-end gap-3">
					{saved && (
						<span className="text-sm text-kaefer-forest-green">Salvo</span>
					)}
					<Button
						variant="primary"
						className="gap-2 font-semibold"
						onClick={() => {
							update(occ.id, { employee: e }, 'Dados do colaborador alterados')
							setSaved(true)
						}}
					>
						<Save className="size-4" /> Salvar
					</Button>
				</div>
			)}
		</Panel>
	)
}

// ─── Classificação ──────────────────────────────────────────────────────────
export const ClassificationSection = () => {
	const { occ, profile, editable } = useOccurrence()
	const update = useOccurrenceMock((s) => s.update)
	const isManager = can(profile, 'confirmClassification')
	const options = CLASSIFICATIONS_BY_TYPE[occ.type]
	const [proposed, setProposed] = useState<ClassificationCode | ''>(
		occ.proposedClassification ?? '',
	)
	const [confirmed, setConfirmed] = useState<ClassificationCode | ''>(
		occ.classification ?? occ.proposedClassification ?? '',
	)
	const [reason, setReason] = useState(occ.classificationDivergenceReason ?? '')
	const [potential, setPotential] = useState<Potential>(occ.potential)
	const diverges = Boolean(confirmed && proposed && confirmed !== proposed)
	const canConfirm =
		occ.status === 'AWAITING_CLASSIFICATION' ||
		occ.status === 'REGISTERED' ||
		occ.status === 'INVESTIGATING'

	return (
		<div className="space-y-4">
			<Panel
				title="Classificação proposta"
				description="Sugerida pelo técnico no cadastro; a confirmação cabe ao Gestor SSMA."
			>
				<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
					{options.map((c) => (
						<div
							key={c}
							className={cn(
								'flex items-center justify-between rounded-lg border-2 px-3 py-2',
								proposed === c
									? 'border-kaefer-dark-grey bg-muted/40'
									: 'border-transparent bg-muted/20',
							)}
						>
							<button
								type="button"
								disabled={
									!editable ||
									(occ.status === 'AWAITING_CLASSIFICATION' && !isManager)
								}
								className="flex-1 text-left text-sm"
								onClick={() => setProposed(c)}
							>
								{CLASSIFICATIONS[c].label}
							</button>
							<ClassificationHelp code={c} />
						</div>
					))}
				</div>
				<PotentialPyramid
					value={potential}
					onChange={setPotential}
					disabled={!editable}
				/>
				{editable && (
					<div className="flex justify-end">
						<Button
							variant="outline"
							onClick={() =>
								update(
									occ.id,
									{ proposedClassification: proposed || null, potential },
									'Classificação proposta alterada',
									{
										diff: {
											field: 'classificação proposta',
											from: occ.proposedClassification ?? '—',
											to: proposed || '—',
										},
									},
								)
							}
						>
							Salvar proposta e potencial
						</Button>
					</div>
				)}
			</Panel>

			<Panel title="Confirmação do Gestor SSMA">
				{occ.classification ? (
					<div className="flex items-center gap-2 text-sm">
						Confirmada: <ClassificationChip code={occ.classification} />
						{occ.classificationDivergenceReason && (
							<span className="text-xs text-muted-foreground">
								· divergiu da proposta: “{occ.classificationDivergenceReason}”
							</span>
						)}
					</div>
				) : (
					<p className="text-sm text-muted-foreground">Ainda não confirmada.</p>
				)}
				{isManager && canConfirm && !occ.classification && (
					<div className="space-y-3">
						<Field label="Classificação final" required>
							<div className="flex items-center gap-1">
								<NativeSelect
									value={confirmed}
									onChange={(e) =>
										setConfirmed(e.target.value as ClassificationCode | '')
									}
								>
									<NativeSelectOption value="">Selecione</NativeSelectOption>
									{options.map((c) => (
										<NativeSelectOption key={c} value={c}>
											{CLASSIFICATIONS[c].label}
										</NativeSelectOption>
									))}
								</NativeSelect>
								{confirmed && <ClassificationHelp code={confirmed} />}
							</div>
						</Field>
						{diverges && (
							<Field label="Justificativa da divergência" required>
								<Textarea
									rows={3}
									value={reason}
									onChange={(e) => setReason(e.target.value)}
									placeholder="Por que a classificação difere da proposta?"
								/>
							</Field>
						)}
						<Button
							variant="primary"
							className="font-semibold"
							disabled={!confirmed || (diverges && reason.trim().length < 5)}
							onClick={() =>
								update(
									occ.id,
									{
										classification: confirmed || null,
										classificationDivergenceReason: diverges
											? reason
											: undefined,
									},
									'Classificação confirmada',
									{
										diff: {
											field: 'classificação',
											from: proposed || '—',
											to: confirmed,
										},
									},
								)
							}
						>
							Confirmar classificação
						</Button>
					</div>
				)}
				{!isManager && !occ.classification && (
					<Notice>Apenas o Gestor SSMA confirma a classificação.</Notice>
				)}
			</Panel>
		</div>
	)
}
